import { Link } from "@/lib/router-compat";
import ResponsiveImage from "@/components/ResponsiveImage";
import { IMG } from "@/lib/optimized-images";
import { Facebook, Linkedin, Youtube, Instagram } from "lucide-react";

const FOOTER_LINKS = {
  Services: [
    { label: "Overview - All Services", href: "/services" },
    { label: "Interior Design", href: "/services/interior-design" },
    { label: "Fit-out & Turnkey", href: "/services/fit-out-contracting" },
    {
      label: "Joinery & Custom Furniture",
      href: "/services/custom-joinery",
    },
    { label: "MEP Solutions", href: "/services/mep-contracting" },
    { label: "Lighting (Luminaire)", href: "/services/architectural-lighting" },
    { label: "Digital Solutions", href: "/services/digital-signage-display" },
    {
      label: "Signage & Metalwork",
      href: "/services/signage-decorative-metalwork",
    },
    {
      label: "Renovation & Refurbishment",
      href: "/services/commercial-renovation-refurbishment",
    },
  ],
  Company: [
    { label: "Home", href: "/" },
    { label: "About Us", href: "/about-us" },
    { label: "Portfolio", href: "/projects" },
    { label: "Insights", href: "/insights" },
    { label: "Careers", href: "/careers" },
    { label: "Contact Us", href: "/contact" },
  ],
  Resources: [
    { label: "Project Portfolio", href: "/projects" },
    { label: "Latest Insights", href: "/insights" },
    { label: "Get a Quote", href: "/contact" },
  ],
};

export default function Footer() {
  return (
    <footer className="bg-[#002b5c] text-white pt-16 sm:pt-24 pb-12 border-t border-white/10 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 sm:gap-12 mb-16 sm:mb-20">
          {/* Brand Info */}
          <div className="lg:col-span-4">
            <Link to="/" className="flex items-center mb-8 group">
              <ResponsiveImage
                rendition={IMG.logo}
                alt="Abacus Design Solutions"
                width={153}
                height={64}
                className="h-16 w-auto transition-transform group-hover:scale-105"
              />
            </Link>
            <p className="text-white/60 text-sm font-body leading-relaxed mb-6 max-w-sm">
              Transforming spaces with innovative interior design and turnkey
              contracting solutions across the Middle East. Excellence in every
              detail.
            </p>
            <div className="space-y-3 text-sm font-body text-white/70">
              <div className="flex items-start gap-3">
                <svg
                  className="w-5 h-5 mt-0.5 text-[#ffcc29] shrink-0"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0z"
                  />
                  <circle
                    cx="12"
                    cy="11"
                    r="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <a
                  href="https://maps.app.goo.gl/qBU2gVhWjh7LkzYb8"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-[#ffcc29] transition-colors"
                >
                  Road 5156, Askar, Kingdom of Bahrain
                </a>
              </div>
              <div className="flex items-center gap-3">
                <svg
                  className="w-5 h-5 text-[#ffcc29] shrink-0"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11 11 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
                <a
                  href="tel:+97317271724"
                  className="hover:text-[#ffcc29] transition-colors"
                >
                  +973 1727 1724
                </a>
                <span className="text-white/30">/</span>
                <a
                  href="tel:+97317789689"
                  className="hover:text-[#ffcc29] transition-colors"
                >
                  +973 1778 9689
                </a>
              </div>
              <div className="flex items-center gap-3">
                <svg
                  className="w-5 h-5 text-[#ffcc29] shrink-0"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"
                  />
                </svg>
                <a
                  href="https://wa.me/97333606055"
                  className="hover:text-[#ffcc29] transition-colors"
                >
                  WA +973 3360 6055
                </a>
              </div>
              <div className="flex items-center gap-3">
                <svg
                  className="w-5 h-5 text-[#ffcc29] shrink-0"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                <a
                  href="mailto:info@abacusds.com"
                  className="hover:text-[#ffcc29] transition-colors"
                >
                  info@abacusds.com
                </a>
              </div>
            </div>
            <div className="flex items-center gap-4 mt-4">
              <a
                href="https://www.facebook.com/abacusinteriordecoration"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/40 hover:text-[#ffcc29] transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://www.linkedin.com/company/abacus-interior-decoration-co-w-l-l/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/40 hover:text-[#ffcc29] transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a
                href="https://www.youtube.com/@AbacusInteriorDecoration"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/40 hover:text-[#ffcc29] transition-colors"
                aria-label="YouTube"
              >
                <Youtube className="w-5 h-5" />
              </a>
              <a
                href="https://www.instagram.com/abacusinteriordecoration/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/40 hover:text-[#ffcc29] transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-3 gap-8">
            {Object.entries(FOOTER_LINKS).map(([title, links]) => (
              <div key={title}>
                <h3 className="font-heading font-bold text-sm tracking-widest uppercase mb-8 text-[#ffcc29]">
                  {title}
                </h3>
                <ul className="space-y-4">
                  {links.map((link) => (
                    <li key={link.label}>
                      {link.href.startsWith("/") && !link.href.includes("#") ? (
                        <Link
                          to={link.href}
                          className="text-white/60 hover:text-white text-sm font-body transition-colors hover:translate-x-1 inline-block"
                        >
                          {link.label}
                        </Link>
                      ) : (
                        <a
                          href={link.href}
                          className="text-white/60 hover:text-white text-sm font-body transition-colors hover:translate-x-1 inline-block"
                        >
                          {link.label}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 pt-12 flex flex-col md:flex-row justify-center items-center gap-6">
          <div className="flex flex-col sm:flex-row items-center gap-4 text-[11px] uppercase tracking-widest font-bold text-white text-center">
            <span>© 2026 Abacus Design Solutions. All Rights Reserved.</span>
            <span className="hidden sm:inline text-white/20">•</span>
            <span>
              Designed by{" "}
              <a
                href="https://corai.me"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#ffcc29] hover:underline transition-colors font-extrabold"
              >
                CORAI
              </a>
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
